import { useState, type ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { View } from 'react-native'

import { GradientTypography } from '@/src/elements/text/gradient'
import { Typography } from '@/src/elements/text/Typography'
import {
  FRATERNITY_QUESTION_STEPS,
  type TFraternityQuestionStep,
} from '@/src/features/community/models/joinSteps.types'
import type { useCommunityJoinSteps } from '@/src/features/community/ui/hooks/useCommunityJoinSteps'
import { SubscribeJourneyLayout } from '@/src/features/community/ui/layouts/SubscribeJourneyLayout'
import { FraternityOnboardingCloseModal } from '@/src/features/fraternity/ui/components/FraternityOnboardingCloseModal'

type TFraternityOnboardingWrapperProps = {
  testID?: string
  joinSteps: ReturnType<typeof useCommunityJoinSteps>
  currentStep?: TFraternityQuestionStep
  title?: ReactNode
  subtitle?: ReactNode
  footer?: ReactNode
  children?: ReactNode
  placeholderOptionCount?: number
}

export const FraternityOnboardingWrapper = ({
  testID,
  joinSteps,
  currentStep,
  title,
  subtitle,
  footer,
  children,
}: TFraternityOnboardingWrapperProps): ReactNode => {
  const [isCloseModalVisible, setIsCloseModalVisible] = useState(false)

  const { goToClose, goToPreviousStep, shouldBeDisplayed, stepperOptions } = joinSteps
  const stepNumber = currentStep ? FRATERNITY_QUESTION_STEPS.indexOf(currentStep) + 1 : undefined

  if (!shouldBeDisplayed) return null

  return (
    <>
      <SubscribeJourneyLayout
        testID={testID}
        onBack={goToPreviousStep}
        onClose={() => setIsCloseModalVisible(true)}
        stepperOptions={stepperOptions}
        footer={footer}
      >
        {stepNumber || title || subtitle ? (
          <View className="flex-1 justify-center gap-6">
            <View className="gap-4 px-4">
              {stepNumber != null && (
                <Typography size="sm" contrast="low">
                  <FormattedMessage id="fraternity.step-label" values={{ step: stepNumber }} />
                </Typography>
              )}
              {title && (
                <GradientTypography colors="primary" size="xl" black>
                  {title}
                </GradientTypography>
              )}
              {subtitle && <Typography>{subtitle}</Typography>}
            </View>
            {children}
          </View>
        ) : (
          <View className="flex-1 justify-center">{children}</View>
        )}
      </SubscribeJourneyLayout>
      <FraternityOnboardingCloseModal
        showModal={isCloseModalVisible}
        onCancel={() => setIsCloseModalVisible(false)}
        onConfirm={() => {
          setIsCloseModalVisible(false)
          goToClose()
        }}
      />
    </>
  )
}
